import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveLimits } from '@traaker/shared';
import { runTour, type StepDef, type TourCtx } from './tourRunner';

// Feature tour for the E-Commerce (Product) module, launched from the sidebar's
// E-Commerce info popover — NOT auto-fired, and it never stamps
// `user.tourState.dashboardTourSeenAt` (that flag belongs to the general
// post-onboarding tour in ./useDashboardTour.ts, which only points at this one).
//
// Deliberately far more detailed than a general-tour section: 15 steps that walk
// the real usage order — catalogue → optimisation → visibilité → contenu —
// naming the actual controls and the credit cost of each action.
//
// Anchor robustness: every step with an `element` is ALSO `centered`, so the
// popover is CSS-pinned to the viewport center while the stage cutout highlights
// the widget when it exists. This matters because the module's pages degrade to
// "Créez d'abord un catalogue" alerts when the tenant has no catalogue yet
// (ProductOptimization.tsx, ProductMeasurement.tsx, ProductContent.tsx) — half
// these anchors are simply absent on a first visit, and a centered popover reads
// correctly either way instead of being flung off-screen by driver's fallback.

const ECOMMERCE_STEPS: StepDef[] = [
  // ── Catalogue ─────────────────────────────────────────────────────────
  {
    centered: true,
    route: '/product/catalog',
    title: 'ecomTour.intro.title',
    description: 'ecomTour.intro.desc',
  },
  {
    element: '[data-tour="product-catalog-new"]',
    centered: true,
    route: '/product/catalog',
    title: 'ecomTour.catalogCreate.title',
    description: 'ecomTour.catalogCreate.desc',
  },
  {
    centered: true,
    route: '/product/catalog',
    title: 'ecomTour.catalogDetail.title',
    description: 'ecomTour.catalogDetail.desc',
  },
  {
    centered: true,
    route: '/product/catalog',
    title: 'ecomTour.catalogRefresh.title',
    description: 'ecomTour.catalogRefresh.desc',
  },
  // ── Optimisation ──────────────────────────────────────────────────────
  {
    centered: true,
    route: '/product/optimization',
    title: 'ecomTour.optIntro.title',
    description: 'ecomTour.optIntro.desc',
  },
  {
    element: '[data-tour="product-opt-catalogue"]',
    centered: true,
    route: '/product/optimization',
    title: 'ecomTour.optScope.title',
    description: 'ecomTour.optScope.desc',
  },
  {
    element: '[data-tour="product-opt-run"]',
    centered: true,
    route: '/product/optimization',
    title: 'ecomTour.optRun.title',
    description: 'ecomTour.optRun.desc',
  },
  {
    element: '[data-tour="product-opt-distribution"]',
    centered: true,
    route: '/product/optimization',
    title: 'ecomTour.optScores.title',
    description: 'ecomTour.optScores.desc',
  },
  {
    element: '[data-tour="product-opt-sections"]',
    centered: true,
    route: '/product/optimization',
    title: 'ecomTour.optWording.title',
    description: 'ecomTour.optWording.desc',
  },
  // ── Visibilité GEO (code name: Audience) ──────────────────────────────
  {
    centered: true,
    route: '/product/measurement',
    title: 'ecomTour.measureIntro.title',
    description: 'ecomTour.measureIntro.desc',
  },
  {
    element: '[data-tour="product-measure-new"]',
    centered: true,
    route: '/product/measurement',
    title: 'ecomTour.measureCreate.title',
    description: 'ecomTour.measureCreate.desc',
  },
  {
    centered: true,
    route: '/product/measurement',
    title: 'ecomTour.measureTabs.title',
    description: 'ecomTour.measureTabs.desc',
  },
  {
    centered: true,
    route: '/product/measurement',
    title: 'ecomTour.measureCadence.title',
    description: 'ecomTour.measureCadence.desc',
  },
  // ── Création de contenu ───────────────────────────────────────────────
  {
    element: '[data-tour="product-content-sources"]',
    centered: true,
    route: '/product/content',
    title: 'ecomTour.contentIntro.title',
    description: 'ecomTour.contentIntro.desc',
  },
  {
    centered: true,
    route: '/product/content',
    title: 'ecomTour.wrapUp.title',
    description: 'ecomTour.wrapUp.desc',
  },
];

export function useEcommerceTour() {
  const { tenant } = useAuth();
  const navigate = useNavigate();

  return useCallback(() => {
    if (!tenant) return;
    const ctx: TourCtx = {
      flags: tenant.settings?.featureFlags ?? {},
      limits: resolveLimits(tenant.tier, tenant.settings?.quotaOverrides),
      tier: tenant.tier,
    };
    runTour(ECOMMERCE_STEPS, ctx, { navigate });
  }, [tenant, navigate]);
}
